import classnames from 'classnames/bind'
import dynamic from 'next/dynamic'
import { useTranslate } from 'react-polyglot'
import { ALERT_CODES } from '~/@types/alert'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGender } from '~/@types/gender'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import {
  useCustomerContext,
  useDeleteCustomerAddress,
} from '@unlikelystudio/react-ecommerce-hooks'

import LoadingComponent from '~/components/Abstracts/LoadingComponent'
import AddressForm, {
  AddressFormProps,
} from '~/components/Account/Logged/AddressBook/AddressForm'
import InlineCta from '~/components/UI/InlineCta'
import { RichTextBlock } from '~/components/UI/RichText'

import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'

import useAlerts from '~/hooks/useAlerts'

import joinTexts from '~/utils/join-texts'

import { ACCOUNT } from '~/data/dictionary'

import css from './styles.module.scss'

const PopIn = dynamic(() => import('~/components/UI/Popin'), {
  loading: () => <LoadingComponent />,
})

const cx = classnames.bind(css)

const computedText = (values: unknown[]) => joinTexts(values, ' ')

export type AddressCardProps = {
  className?: string
  isDefault?: boolean
  id?: string
  name?: string
  firstName?: string
  lastName?: string
  gender?: GlobalGender
  address1?: string
  address2?: string
  city?: string
  zip?: string
  country?: string
  countryCodeV2?: string
  phone?: string
  update?: AddressFormProps['update']
}

export type AddressItemProps = AddressCardProps & {
  className?: string
  isAdd?: boolean
  addAddressTitle?: RichTextBlock
  updateAddressTitle?: RichTextBlock
}

function AddressItem({
  className,
  isAdd,
  isDefault,
  addAddressTitle,
  updateAddressTitle,
  ...address
}: AddressItemProps) {
  const { id, firstName, lastName, address1, city, zip, country, phone } =
    address

  const { customerAccessToken } = useCustomerContext()
  const t = useTranslate()

  const linkStyle = useStyle({
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Black,
    textPreset: GlobalTextPreset.Cta12Grotesk,
  })

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Black,
  })

  const { add } = usePopIn()
  const triggerAlert = useAlerts()

  const { mutate } = useDeleteCustomerAddress({
    onSuccess: () => triggerAlert(ALERT_CODES.ENTITY_SAVED),
    onError: () => {
      triggerAlert(ALERT_CODES.SERVER_ERROR)
    },
  })
  const handleAddressPopinAction = (props: AddressFormProps) => {
    add(
      <PopIn className={cx(css.AddressPopin, css.popin)} isCloseButton={true}>
        <AddressForm
          className={css.addressForm}
          title={props?.update ? updateAddressTitle : addAddressTitle}
          {...(props ?? {})}
        />
      </PopIn>,
    )
  }

  const computedFullName = computedText([firstName, lastName])
  const computedCity = computedText([zip, city])
  const content = (
    <div className={css.wrapper}>
      {isAdd && (
        <>
          <div className={cx(linkStyle, css.placeHolderLink)}>
            <span>{t(ACCOUNT.ADD_ADDRESS)}</span>
            (+)
          </div>
        </>
      )}

      {!isAdd && (
        <>
          <div className={cx(css.empty)}></div>
          <div className={cx(css.content)}>
            {computedFullName && (
              <p className={textStyle}>{computedFullName}</p>
            )}
            {address1 && <p className={textStyle}>{address1}</p>}
            {computedCity && <p className={textStyle}>{computedCity}</p>}
            {country && <p className={textStyle}>{country}</p>}
            {phone && <p className={textStyle}>{phone}</p>}
          </div>
          <div className={cx(css.buttons)}>
            <InlineCta
              className={cx(linkStyle, css.link)}
              onClick={() => {
                handleAddressPopinAction({
                  update: true,
                  address,
                })
              }}>
              {t(ACCOUNT.EDIT_ADDRESS)}
            </InlineCta>
            <InlineCta
              className={cx(linkStyle, css.link)}
              onClick={() => mutate({ id, customerAccessToken })}>
              {t(ACCOUNT.DELETE_ADDRESS)}
            </InlineCta>
          </div>
        </>
      )}
    </div>
  )
  return isAdd ? (
    <button
      className={cx(css.AddressItem, className, css.ratio, { isAdd })}
      onClick={() => {
        handleAddressPopinAction({})
      }}>
      {content}
    </button>
  ) : (
    <div className={cx(css.AddressItem, className, css.ratio, { isAdd })}>
      {content}
    </div>
  )
}

AddressItem.defaultProps = {
  isAdd: false,
}

export default AddressItem
